import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MessageCircle, Send, Plus, Loader2 } from "lucide-react";
import { t as tr, useLang } from "../lib/i18n";
import { setUnreadCount } from "../components/Navbar";

// Small formatters for timestamps. Keep everything ISO-8601 on the wire
// and format at render time so language + timezone come from the browser.
const _fullDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const _relative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24);
  if (dd < 7) return `${dd}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

// Add messaging-specific translation keys inline via a tiny helper — keeps
// the i18n dictionary file clean while covering messaging-only strings.
const msg = {
  eyebrow: { eng: "Inbox", spa: "Bandeja de entrada", fre: "Boîte de réception", ger: "Posteingang", ita: "Posta in arrivo", por: "Caixa de entrada" },
  new_thread: { eng: "Start a new thread", spa: "Iniciar una conversación", fre: "Nouvelle conversation", ger: "Neue Unterhaltung", ita: "Nuova conversazione", por: "Nova conversa" },
  reader_email: { eng: "Reader's email", spa: "Correo del lector", fre: "E-mail du lecteur", ger: "E-Mail des Lesers", ita: "Email del lettore", por: "E-mail do leitor" },
  start: { eng: "Start", spa: "Iniciar", fre: "Démarrer", ger: "Starten", ita: "Inizia", por: "Iniciar" },
  no_threads: { eng: "No conversations yet.", spa: "Aún no hay conversaciones.", fre: "Pas encore de conversations.", ger: "Noch keine Unterhaltungen.", ita: "Ancora nessuna conversazione.", por: "Sem conversas ainda." },
  send: { eng: "Send", spa: "Enviar", fre: "Envoyer", ger: "Senden", ita: "Invia", por: "Enviar" },
  reply_placeholder: { eng: "Write a reply…", spa: "Escribe una respuesta…", fre: "Écris une réponse…", ger: "Antwort schreiben…", ita: "Scrivi una risposta…", por: "Escreve uma resposta…" },
  select_thread: { eng: "Pick a conversation, or start a new one.", spa: "Elige una conversación o empieza una nueva.", fre: "Choisis une conversation.", ger: "Wähle eine Unterhaltung.", ita: "Scegli una conversazione.", por: "Escolhe uma conversa." },
  say_hello: { eng: "Say hello. New conversations are private between you two.", spa: "Saluda. Las nuevas conversaciones son privadas entre vosotros dos.", fre: "Dis bonjour.", ger: "Sag hallo.", ita: "Saluta.", por: "Diz olá." },
  type_msg: { eng: "Type a message…", spa: "Escribe un mensaje…", fre: "Écris un message…", ger: "Nachricht eingeben…", ita: "Scrivi un messaggio…", por: "Escreve uma mensagem…" },
  send_btn: { eng: "Send", spa: "Enviar", fre: "Envoyer", ger: "Senden", ita: "Invia", por: "Enviar" },
  unknown_peer: { eng: "Unknown", spa: "Desconocido", fre: "Inconnu", ger: "Unbekannt", ita: "Sconosciuto", por: "Desconhecido" },
  conversation: { eng: "Conversation", spa: "Conversación", fre: "Conversation", ger: "Unterhaltung", ita: "Conversazione", por: "Conversa" },
};
function m(key) {
  const lang = (typeof localStorage !== "undefined" && localStorage.getItem("readsLang")) || "eng";
  return msg[key]?.[lang] || msg[key]?.eng || key;
}

export default function MessagingPage() {
  useLang();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [newPeer, setNewPeer] = useState("");
  const [opening, setOpening] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const bottomRef = useRef(null);
  const listRef = useRef(null);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
    } catch (e) { /* surfaced by other UI */ }
  };

  useEffect(() => { loadThreads(); }, []);

  useEffect(() => {
    if (!threadId) { setMessages([]); return; }
    api.get(`/messages/threads/${threadId}/messages`)
      .then(({ data }) => setMessages(data))
      .catch(() => setMessages([]));
    // Mark this thread as read the moment the pane opens. Also update the
    // local threads list + global unread count so the red dot goes away
    // without waiting for the next 45-second poll.
    api.post(`/messages/threads/${threadId}/read`).then(() => {
      setThreads((prev) => prev.map((th) => th.id === threadId ? { ...th, unread: false } : th));
      // Recount from the (locally-updated) list.
      setThreads((cur) => {
        const n = cur.filter((th) => th.unread && th.id !== threadId).length;
        setUnreadCount(n);
        return cur;
      });
    }).catch(() => { /* no-op */ });
  }, [threadId]);

  useEffect(() => {
    // Scroll the messages CONTAINER only (not the whole document) so the
    // outer page stays put when the user opens a thread.
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const openThread = async (e) => {
    e?.preventDefault();
    if (!newPeer.trim()) return;
    setOpening(true);
    try {
      const { data } = await api.post("/messages/threads", { peer_email: newPeer.trim().toLowerCase() });
      setNewPeer("");
      setShowCompose(false);
      await loadThreads();
      navigate(`/messages/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOpening(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !threadId) return;
    setDraft("");
    try {
      const { data } = await api.post(`/messages/threads/${threadId}/messages`, { body });
      setMessages((prev) => [...prev, data]);
      loadThreads();
    } catch (e) {
      setDraft(body);
      toast.error(formatApiError(e));
    }
  };

  const activeThread = threads.find((t) => t.id === threadId);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-10 py-10 fade-up" data-testid="messages-page">
      <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030]">{m("eyebrow")}</div>
      <div className="flex items-end justify-between gap-4 mb-8">
        <h1 className="font-heading text-4xl md:text-5xl text-[#0A0A0A] mt-3 tracking-tight">{tr("nav.messages")}</h1>
        <button
          onClick={() => setShowCompose((v) => !v)}
          className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm transition-colors"
          data-testid="new-thread-toggle"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> {m("new_thread")}
        </button>
      </div>

      {showCompose && (
        <form onSubmit={openThread} className="mb-6 flex gap-2 max-w-xl" data-testid="new-thread-form">
          <input
            value={newPeer}
            onChange={(e) => setNewPeer(e.target.value)}
            placeholder={m("reader_email")}
            type="email"
            className="flex-1 bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
            data-testid="new-thread-email"
          />
          <button type="submit" disabled={opening} className="border border-[rgba(10,10,10,0.18)] bg-[#FAF5E6] hover:bg-black/5 text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-4 py-2.5 rounded-sm disabled:opacity-50" data-testid="new-thread-submit">
            {opening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : m("start")}
          </button>
        </form>
      )}

      <div className="grid md:grid-cols-[280px_1fr] gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)] min-h-[60vh]">
        {/* Threads sidebar */}
        <aside className="bg-[#FAF5E6] p-3 overflow-y-auto" data-testid="threads-list">
          {threads.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#4A4030]">
              <MessageCircle className="w-6 h-6 mx-auto mb-3 text-[#8C7F65]" strokeWidth={1.5} />
              {m("no_threads")}
            </div>
          ) : threads.map((th) => {
            const active = th.id === threadId;
            return (
              <button
                key={th.id}
                onClick={() => navigate(`/messages/${th.id}`)}
                className={`w-full text-left p-3 rounded-sm transition-colors mb-1 relative ${active ? "bg-[#0A0A0A] text-[#F2EAD3]" : "hover:bg-black/5 text-[#0A0A0A]"}`}
                data-testid={`thread-${th.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className={`font-heading text-base truncate ${th.unread && !active ? "font-bold" : ""}`}>{th.peer?.name || th.peer?.email || m("unknown_peer")}</div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {th.unread && !active && (
                      <span className="w-2 h-2 rounded-full bg-[#C1272D]" data-testid={`thread-unread-${th.id}`} aria-label="Unread" />
                    )}
                    <span className={`font-mono text-[10px] ${active ? "text-[#F2EAD3]/70" : "text-[#8C7F65]"}`} title={_fullDate(th.last_message_at)}>
                      {_relative(th.last_message_at)}
                    </span>
                  </div>
                </div>
                <div className={`text-xs truncate mt-0.5 ${active ? "text-[#F2EAD3]/70" : "text-[#525252]"} ${th.unread && !active ? "font-semibold text-[#0A0A0A]" : ""}`}>
                  {th.last_message || "…"}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Conversation pane */}
        <section className="bg-[#FAF5E6] flex flex-col" data-testid="conversation-pane">
          {!threadId ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#4A4030] p-10">
              {m("select_thread")}
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-[rgba(10,10,10,0.08)] flex items-center justify-between">
                <div>
                  <div className="font-heading text-lg text-[#0A0A0A]">{activeThread?.peer?.name || m("conversation")}</div>
                  <div className="font-mono uppercase tracking-widest text-[10px] text-[#525252]">{activeThread?.peer?.email}</div>
                </div>
              </div>
              <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-list">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-[#4A4030] py-10">{m("say_hello")}</div>
                ) : messages.map((m2) => {
                  const mine = m2.from_id === user?.id;
                  // Mine: white paper-like bubble with BLACK ink. Theirs: black bubble with WHITE ink.
                  const bubble = mine
                    ? "bg-white text-[#0A0A0A] border border-[rgba(10,10,10,0.10)]"
                    : "bg-[#0A0A0A] text-white";
                  return (
                    <div key={m2.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`msg-${m2.id}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 text-sm rounded-3xl ${mine ? "rounded-br-md" : "rounded-bl-md"} ${bubble}`}>
                        {m2.body}
                      </div>
                      <div
                        className="font-mono text-[10px] text-[#8C7F65] mt-1 px-2"
                        title={_fullDate(m2.created_at)}
                        data-testid={`msg-time-${m2.id}`}
                      >
                        {_relative(m2.created_at)}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <form onSubmit={send} className="border-t border-[rgba(10,10,10,0.08)] p-3 flex gap-2" data-testid="message-form">
                <input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={m("type_msg")}
                  className="flex-1 bg-white border border-[rgba(10,10,10,0.12)] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black"
                  data-testid="message-input"
                />
                <button type="submit" disabled={!draft.trim()} className="inline-flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-4 rounded-sm disabled:opacity-50" data-testid="message-send">
                  <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> {m("send_btn")}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
